// The department's rules as the portal runs them today (spec C4, C5, C14). Phase 1 keeps most in code;
// Settings shows them read only until editing lands (shell/placeholders.ts: settings.editUrgency,
// settings.editDefaults). Where each value lives in code, so a change there is a change here:
//   windows      apps/server/src/ams360/window.ts (60), renewals.ts missedDays (30)
//   bands        apps/web/src/renewals/renewal-lines.ts band() (45, 30, 14)
//   attention    renewal-lines.ts attentionReason(); server fetch/view.ts attemptsBeforeManual (2)
//   increase     stored in portal.settings (increase_rule), read by the server on every request;
//                the page reads it from GET /api/settings/increase-rule, not from here. The increase
//                template's days: apps/server/src/emails/draft.ts increaseTemplateMoreThanDays (30)
//   2FA wait     apps/server/src/mail/two-factor.ts defaultWaitMs (90 s)
// settings.test.tsx checks the web side against these numbers.
import { statuses } from '../renewals/renewal-lines';
import type { RenewalStatus } from '../auth/api';

export const windows = { review: 60, working: 45, missed: 30 };
export const bands = { alert: 30, urgent: 14 };
export const attention = { daysLeft: 14, needPaymentDays: 10, fetchFailures: 2 };
export const increase = { templateMoreThanDays: 30 };
export const fetching = { codeWaitSeconds: 90, attempts: 2 };

const stageLabels = { open: 'Open', waiting: 'Waiting', withae: 'With AE', closed: 'Closed' };

/** The department sheet's value for each status (spec C4). */
const sheetValues: Record<RenewalStatus, string> = {
  pending: 'Still Pending',
  requested: 'Still Pending, plus comment',
  need_payment: 'Completed/Need Payment',
  quote: 'Comment: proposed to client, waiting on bind order',
  increase: 'Rewrite/Increase',
  non_renewal: 'Rewrite/NRWL',
  noc: 'Rewrite/NOC',
  done: 'Completed/Done',
  cancelled: 'Cancelled at Rwl/Declined',
};

/** The nine built-in statuses in order, with stage and sheet value (Settings, Statuses). */
export const statusRows = (Object.keys(statuses) as RenewalStatus[]).map((key) => ({
  key,
  label: statuses[key].label,
  pill: statuses[key].pill,
  stage: stageLabels[statuses[key].stage],
  sheet: sheetValues[key],
}));

/** The urgency bands (Settings, Urgency and window), with the days pill each uses. */
export const bandRows = [
  {
    label: 'Upcoming',
    days: `${windows.review} to ${windows.working + 1}`,
    pill: 'pill dpill u-grey',
  },
  { label: 'Working', days: `${windows.working} to ${bands.alert + 1}`, pill: 'pill dpill u-grey' },
  { label: 'Alert', days: `${bands.alert} to ${bands.urgent + 1}`, pill: 'pill dpill u-amber' },
  { label: 'Urgent', days: `${bands.urgent} to 0`, pill: 'pill dpill u-red' },
];

/** What puts a renewal under Needs attention (spec C5). */
export const attentionRules = [
  `${attention.daysLeft} days or fewer left`,
  'Expired and not closed',
  `Need payment with ${attention.needPaymentDays} days or fewer left`,
  `Fetch failed ${attention.fetchFailures} times`,
  'Login rejected by the carrier',
];
